/**
 * Asset Routes
 *
 * Route definitions for the assets module.
 */

import { Permission } from '@condo/domain';
import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import type { RamRouteObject } from '@/shared/lib/permissions';

const AssetDetailLayout = lazy(() => import('@/features/asset/detail').then(module => ({ default: module.AssetDetailLayout })));
const AssetLegacyRedirect = lazy(() => import('@/pages/assets/AssetLegacyRedirect'));
const AssetTransformationRedirect = lazy(() => import('@/pages/assets/AssetTransformationRedirect'));
const AssetOverview = lazy(() => import('@/pages/assets/AssetOverview'));
const AssetInvestmentCases = lazy(() => import('@/pages/assets/AssetInvestmentCases'));
const AssetInvestmentCaseComparison = lazy(() => import('@/pages/assets/AssetInvestmentCaseComparison'));

const AssetFilesLayout = lazy(() => import('@/pages/assets/AssetFilesLayout'));
const AssetFilesDataRooms = lazy(() => import('@/pages/assets/AssetFilesDataRooms'));
const AssetFilesDocumentClassifier = lazy(() => import('@/pages/assets/AssetFilesDocumentClassifier'));
const AssetFilesMedia = lazy(() => import('@/pages/assets/AssetFilesMedia'));

const AssetMilestonesLayout = lazy(() => import('@/pages/assets/AssetMilestonesLayout'));
const AssetMilestonesReporting = lazy(() => import('@/pages/assets/AssetMilestonesReporting'));
const AssetMilestonesProjectPlan = lazy(() => import('@/pages/assets/AssetMilestonesProjectPlan'));
const AssetMilestonesProjectPlanSnapshots = lazy(() => import('@/pages/assets/AssetMilestonesProjectPlanSnapshots'));

const AssetApprovalsLayout = lazy(() => import('@/pages/assets/AssetApprovalsLayout'));
const AssetEnvelopesPage = lazy(() => import('@/pages/assets/AssetEnvelopesPage'));
const AssetEnvelopeDetailPage = lazy(() => import('@/pages/assets/AssetEnvelopeDetailPage'));
const AssetEnvelopeCreatePage = lazy(() => import('@/pages/assets/AssetEnvelopeCreatePage'));
const AssetEnvelopeAdditionalDocumentPage = lazy(() => import('@/pages/assets/AssetEnvelopeAdditionalDocumentPage'));

const AssetAiImports = lazy(() => import('@/pages/assets/AssetAiImports'));

const AssetTransformationLayout = lazy(() => import('@/pages/assets/AssetTransformationLayout'));
const AssetRenovationProjects = lazy(() => import('@/pages/assets/AssetRenovationProjects'));
const AssetRenovationCostTracking = lazy(() => import('@/pages/assets/AssetRenovationCostTracking'));

const AssetAddendumDetailPage = lazy(() => import('@/pages/assets/AssetAddendumDetailPage'));
const AssetContractManagementOverview = lazy(() => import('@/pages/assets/AssetContractManagementOverview'));
const AssetContractDetailPage = lazy(() => import('@/pages/assets/AssetContractDetailPage'));
const AssetElementsIfc = lazy(() => import('@/pages/assets/AssetElementsIfc'));
const IfcElements = lazy(() => import('@/pages/assets/IfcElements'));
const IfcElementsImport = lazy(() => import('@/pages/assets/IfcElementsImport'));

const AssetOccupancyLayout = lazy(() => import('@/pages/assets/AssetOccupancyLayout'));
const AssetTenancies = lazy(() => import('@/pages/assets/AssetTenancies'));
const AssetContracts = lazy(() => import('@/pages/assets/AssetContracts'));

const AssetDetailsLayout = lazy(() => import('@/pages/assets/AssetDetailsLayout'));
const AssetDetailsPropertyTab = lazy(() => import('@/pages/assets/AssetDetailsPropertyTab'));
const AssetDetailsLocationTab = lazy(() => import('@/pages/assets/AssetDetailsLocationTab'));
const AssetDetailsLocationAnalysisTab = lazy(() => import('@/pages/assets/AssetDetailsLocationAnalysisTab'));
const AssetDetailsRentDataTab = lazy(() => import('@/pages/assets/AssetDetailsRentDataTab'));
const AssetDetailsSourcingTab = lazy(() => import('@/pages/assets/AssetDetailsSourcingTab'));
const AssetDetailsScreeningTab = lazy(() => import('@/pages/assets/AssetDetailsScreeningTab'));
const AssetDetailsFinancialTab = lazy(() => import('@/pages/assets/AssetDetailsFinancialTab'));

const AssetNotesPage = lazy(() => import('@/pages/assets/AssetNotesPage'));
const AssetContactsPage = lazy(() => import('@/pages/assets/AssetContactsPage'));
const AssetInvestmentCaseDetail = lazy(() => import('@/pages/assets/AssetInvestmentCaseDetail'));
const AssetICTeaserPrint = lazy(() => import('@/pages/assets/AssetICTeaserPrint'));
const AssetStateTransitionEmbed = lazy(() => import('@/pages/assets/AssetStateTransitionEmbed'));
const AssetBaseInvestmentCaseConfigure = lazy(() => import('@/pages/assets/AssetBaseInvestmentCaseConfigure'));
const AssetAmInvestmentCasesStub = lazy(() => import('@/pages/assets/AssetAmInvestmentCasesStub'));
const AssetEstates = lazy(() => import('@/pages/assets/AssetEstates'));
const AssetExternalUnits = lazy(() => import('@/pages/assets/AssetExternalUnits'));
const AssetRentRoll = lazy(() => import('@/pages/assets/AssetRentRoll'));
const AssetRentRollVersionDetail = lazy(() => import('@/pages/assets/AssetRentRollVersionDetail'));
const AssetAccounting = lazy(() => import('@/pages/assets/AssetAccounting'));
const AssetControlling = lazy(() => import('@/pages/assets/AssetControlling'));
const AssetCapexEeCalculator = lazy(() => import('@/pages/assets/AssetCapexEeCalculator'));
const AssetCapexEeScenarioDetail = lazy(() => import('@/pages/assets/AssetCapexEeScenarioDetail'));
const AssetHistoryLayout = lazy(() => import('@/pages/assets/AssetHistoryLayout'));
const AssetProcessesPage = lazy(() => import('@/pages/assets/AssetProcessesPage'));
const AssetHistoryPage = lazy(() => import('@/pages/assets/AssetHistoryPage'));
const AssetProcessesRedirect = lazy(() => import('@/pages/assets/AssetProcessesRedirect'));
const AssetTasksPage = lazy(() => import('@/pages/assets/AssetTasksPage'));
const AssetCalendarPage = lazy(() => import('@/pages/assets/AssetCalendarPage'));
const AssetDueDiligencePage = lazy(() => import('@/pages/assets/AssetDueDiligencePage'));
const AssetPlacementPage = lazy(() => import('@/pages/assets/AssetPlacementPage'));

const AssetConstructionPlansPage = lazy(() => import('@/pages/assets/AssetConstructionPlansPage'));
const AssetConstructionPlanDetailPage = lazy(() => import('@/pages/assets/AssetConstructionPlanDetailPage'));
const AssetConstructionPlanCreateHubPage = lazy(() => import('@/pages/assets/AssetConstructionPlanCreateHubPage'));
const AssetConstructionPlanCreatePage = lazy(() => import('@/pages/assets/AssetConstructionPlanCreatePage'));
const AssetConstructionPlanBulkCreatePage = lazy(() => import('@/pages/assets/AssetConstructionPlanBulkCreatePage'));
const AssetConstructionPlanNewVersionPage = lazy(() => import('@/pages/assets/AssetConstructionPlanNewVersionPage'));
const AssetConstructionPlanComparePage = lazy(() => import('@/pages/assets/AssetConstructionPlanComparePage'));

export const assetStandaloneRoutes: RamRouteObject[] = [
    {
        path: 'investment-case-teaser/:mfhInvestmentCaseId/preview',
        element: <AssetICTeaserPrint />,
        permissions: [Permission.MFHInvestmentCaseView],
    },
    // Cockpit overlays this on its asset page for the Action Trigger (outside `App`, so no RAM chrome).
    {
        path: 'embed/assets/:id/state-transition',
        element: <AssetStateTransitionEmbed />,
        permissions: [Permission.MFHMFHView],
    },
];

export const assetRoutes: RamRouteObject[] = [
    {
        path: 'assets/:id',
        element: <AssetDetailLayout />,
        children: [
            { index: true, element: <AssetOverview />, permissions: [Permission.MFHMFHView] },
            { path: 'overview', element: <AssetOverview />, permissions: [Permission.MFHMFHView], name: 'asset.overview' },

            {
                path: 'details',
                element: <AssetDetailsLayout />,
                children: [
                    { index: true, element: <Navigate to="property" replace /> },
                    { path: 'property', element: <AssetDetailsPropertyTab /> },
                    { path: 'location', element: <AssetDetailsLocationTab /> },
                    { path: 'location-analysis', element: <AssetDetailsLocationAnalysisTab /> },
                    { path: 'rent-data', element: <AssetDetailsRentDataTab /> },
                    { path: 'sourcing', element: <AssetDetailsSourcingTab /> },
                    { path: 'screening', element: <AssetDetailsScreeningTab /> },
                    { path: 'financial', element: <AssetDetailsFinancialTab /> },
                ],
            },

            { path: 'notes', element: <AssetNotesPage /> },
            { path: 'contacts', element: <AssetContactsPage /> },

            {
                path: 'files',
                element: <AssetFilesLayout />,
                children: [
                    { index: true, element: <Navigate to="data-rooms" replace /> },
                    { path: 'data-rooms', element: <AssetFilesDataRooms /> },
                    { path: 'media', element: <AssetFilesMedia /> },
                    { path: 'document-classifier', element: <AssetFilesDocumentClassifier /> },
                ],
            },

            {
                path: 'milestones',
                element: <AssetMilestonesLayout />,
                children: [
                    { index: true, element: <Navigate to="reporting" replace /> },
                    { path: 'reporting', element: <AssetMilestonesReporting /> },
                    { path: 'project-plan', element: <AssetMilestonesProjectPlan /> },
                    { path: 'project-plan-snapshots', element: <AssetMilestonesProjectPlanSnapshots /> },
                ],
            },

            {
                path: 'approvals',
                element: <AssetApprovalsLayout />,
                children: [
                    { index: true, element: <AssetEnvelopesPage /> },
                    { path: 'create', element: <AssetEnvelopeCreatePage /> },
                    { path: ':envelopeId', element: <AssetEnvelopeDetailPage /> },
                    { path: ':envelopeId/additional-document', element: <AssetEnvelopeAdditionalDocumentPage /> },
                ],
            },

            { path: 'ai-imports', element: <AssetAiImports /> },
            { path: 'tasks', element: <AssetTasksPage /> },
            {
                path: 'history',
                element: <AssetHistoryLayout />,
                children: [
                    { index: true, element: <Navigate to="events" replace /> },
                    { path: 'events', element: <AssetHistoryPage /> },
                    { path: 'processes', element: <AssetProcessesPage /> },
                ],
            },
            { path: 'processes', element: <AssetProcessesRedirect /> },
            { path: 'calendar', element: <AssetCalendarPage /> },

            {
                path: 'transformation',
                element: <AssetTransformationLayout />,
                children: [
                    { index: true, element: <Navigate to="projects" replace /> },
                    { path: 'projects', element: <AssetRenovationProjects /> },
                    { path: 'cost-tracking', element: <AssetRenovationCostTracking /> },
                    { path: 'addenda', element: <Navigate to="../contracts" replace /> },
                    { path: 'addenda/:addendumId', element: <AssetAddendumDetailPage /> },
                    { path: 'contracts', element: <AssetContractManagementOverview /> },
                    { path: 'contracts/:contractId', element: <AssetContractDetailPage /> },
                    { path: 'contracts/:contractId/addenda/:addendumId', element: <AssetAddendumDetailPage /> },
                    {
                        path: 'elements-ifc',
                        element: <AssetElementsIfc />,
                        children: [
                            { index: true, element: <IfcElements /> },
                            { path: 'import', element: <IfcElementsImport /> },
                        ],
                    },
                ],
            },

            { path: 'renovation', element: <AssetTransformationRedirect targetPath="projects" /> },
            { path: 'renovation/projects', element: <AssetTransformationRedirect targetPath="projects" /> },
            { path: 'renovation/cost-tracking', element: <AssetTransformationRedirect targetPath="cost-tracking" /> },
            { path: 'addenda', element: <AssetTransformationRedirect targetPath="addenda" /> },
            { path: 'addenda/:addendumId', element: <AssetTransformationRedirect targetPath="addenda/:addendumId" /> },
            { path: 'contracts', element: <AssetTransformationRedirect targetPath="contracts" /> },
            { path: 'contracts/:contractId', element: <AssetTransformationRedirect targetPath="contracts/:contractId" /> },
            { path: 'elements-ifc', element: <AssetTransformationRedirect targetPath="elements-ifc" /> },
            {
                path: 'elements-ifc/import',
                element: <AssetTransformationRedirect targetPath="elements-ifc/import" />,
            },

            { path: 'investment-cases', element: <AssetInvestmentCases /> },
            { path: 'investment-cases/compare', element: <AssetInvestmentCaseComparison /> },
            { path: 'accounting', element: <AssetAccounting /> },
            { path: 'controlling', element: <AssetControlling /> },
            {
                path: 'capex-ee-calculator',
                element: <AssetCapexEeCalculator />,
                permissions: [Permission.MFHCapexEeScenarioView],
            },
            {
                path: 'capex-ee-calculator/:scenarioId',
                element: <AssetCapexEeScenarioDetail />,
                permissions: [Permission.MFHCapexEeScenarioView],
            },
            { path: 'estates', element: <AssetEstates /> },
            { path: 'external-units', element: <AssetExternalUnits /> },
            { path: 'rent-roll', element: <AssetRentRoll /> },
            { path: 'due-diligence', element: <AssetDueDiligencePage /> },
            // Its own route so a refresh stays on the checklist instead of falling back to documents.
            { path: 'due-diligence/checklist', element: <AssetDueDiligencePage /> },
            { path: 'placement', element: <AssetPlacementPage /> },
            {
                path: 'construction-plans/new/single',
                element: <AssetConstructionPlanCreatePage />,
                permissions: [Permission.ConstructionPlanCreate],
                name: 'asset.constructionPlans.create.single',
            },
            {
                path: 'construction-plans/new/bulk',
                element: <AssetConstructionPlanBulkCreatePage />,
                permissions: [Permission.ConstructionPlanCreate],
                name: 'asset.constructionPlans.create.bulk',
            },
            {
                path: 'construction-plans/new',
                element: <AssetConstructionPlanCreateHubPage />,
                permissions: [Permission.ConstructionPlanCreate],
                name: 'asset.constructionPlans.create',
            },
            {
                path: 'construction-plans/compare',
                element: <AssetConstructionPlanComparePage />,
                permissions: [Permission.ConstructionPlanView],
                name: 'asset.constructionPlans.compare',
            },
            {
                path: 'construction-plans/:planId/new-version',
                element: <AssetConstructionPlanNewVersionPage />,
                permissions: [Permission.ConstructionPlanCreate],
                name: 'asset.constructionPlans.newVersion',
            },
            {
                path: 'construction-plans/:planId',
                element: <AssetConstructionPlanDetailPage />,
                permissions: [Permission.ConstructionPlanView],
                name: 'asset.constructionPlans.detail',
            },
            {
                path: 'construction-plans',
                element: <AssetConstructionPlansPage />,
                permissions: [Permission.ConstructionPlanView],
                name: 'asset.constructionPlans',
            },
            { path: 'rent-roll/:versionId', element: <AssetRentRollVersionDetail /> },
            { path: 'investment-cases/:icId', element: <AssetInvestmentCaseDetail /> },
            {
                path: 'base-investment-cases/:icId/configure',
                element: <AssetBaseInvestmentCaseConfigure />,
                permissions: [Permission.MFHInvestmentCaseCreate],
            },
            { path: 'am-investment-cases', element: <AssetAmInvestmentCasesStub /> },

            {
                path: 'occupancy',
                element: <AssetOccupancyLayout />,
                children: [
                    { index: true, element: <Navigate to="tenancies" replace /> },
                    { path: 'units', element: <Navigate to="../tenancies" replace /> },
                    { path: 'tenancies', element: <AssetTenancies /> },
                    { path: 'contracts', element: <AssetContracts /> },
                ],
            },

            { path: 'units', element: <AssetLegacyRedirect targetPath="occupancy/tenancies" /> },
            { path: 'tenancies', element: <AssetLegacyRedirect targetPath="occupancy/tenancies" /> },
            { path: 'contracts', element: <AssetLegacyRedirect targetPath="occupancy/contracts" /> },
        ],
    },
];
